// IMPORTANT: HelpPopupContent is only for internal use.
// Consumer should only be using HelpPopup
import { createElement, Fragment, useEffect, useMemo, useState } from 'react';
import { getHelpCenterUrl } from '@sas/nova-core/services';
import { GenericKnownObject } from '@sas/react-svg';
import { setAlpha } from '../../../design-tokens';
import { useMounted } from '../../../hooks/useMounted';
import { getLanguage, useTextLocalizer } from '../../../locale/useTextLocalizer';
import { markEvent } from '../../../marked-events';
import { renderInlineCssVars, useDesignTokens } from '../../../theme';
import { Icon } from '../../Icon';
import { SVG, useResolveSVGIcon } from '../../SVG';
import cn from './HelpPopupContent.css.json';
import './HelpPopupContent.css';
import { jsx as _jsx } from "react/jsx-runtime";
import { createElement as _createElement } from "react";
export const HelpPopupContent = props => {
  const localizeHelpContent = useTextLocalizer(props.library);
  const localizedContent = localizeHelpContent(`${props.contentKey}.help.content.txt`).replace(/\\([^\\])/g, '$1');
  const {
    resolveSVG,
    resolveIcon
  } = useResolveSVGIcon();
  const [importedLinks, setImportedLinks] = useState({});
  const mounted = useMounted();
  useEffect(() => {
    // Parse the incoming html content so we can build the right URLs for any help center links
    const fetchHelpCenterLinks = async () => {
      const parser = new DOMParser();
      const parseResults = parser.parseFromString(localizedContent, 'text/html');
      const helpCenterLinks = [];
      for (const c of parseResults.body.children) {
        findHelpCenterLinks(c, helpCenterLinks);
      }
      const getKeyUrlPair = async topicParams => {
        const url = await getHelpCenterUrl({
          dataProvider: props.dataProvider,
          customParams: {
            locale: getLanguage(),
            ...topicParams
          }
        });
        if (url) {
          const key = getKey(topicParams.docsetId, topicParams.docsetTarget, topicParams.docsetTargetAnchor);
          return {
            key,
            url
          };
        }
      };
      // Go fetch the necessary data and build urls for any help center links we found
      const keyUrlPairs = await Promise.all(helpCenterLinks.map(topicParams => getKeyUrlPair(topicParams)));
      const newImportedLinks = {};
      for (const keyUrlPair of keyUrlPairs) {
        if (keyUrlPair) {
          newImportedLinks[keyUrlPair.key] = keyUrlPair.url;
        }
      }
      mounted.current && setImportedLinks(newImportedLinks);
    };
    fetchHelpCenterLinks();
  }, [props.dataProvider, localizedContent, mounted]);
  const content = useMemo(() => {
    return parseContent(localizedContent, importedLinks, resolveSVG, resolveIcon);
  }, [localizedContent, importedLinks, resolveSVG, resolveIcon]);
  const dt = useDesignTokens();
  const tokens = renderInlineCssVars({
    mg: dt.tokens['space-base-m'],
    'small-mg': dt.tokens['space-base-s'],
    'tiny-mg': dt.tokens['space-base-xs'],
    'none-mg': dt.tokens['space-base-null'],
    pd: dt.tokens['space-base-m'],
    'tiny-pd': dt.tokens['space-base-xs'],
    'small-pd': dt.tokens['space-base-s'],
    'indent-pd': dt.tokens['space-base-xl'],
    'font-size-m': dt.tokens['font-size-body-text-m'],
    'line-height': dt.tokens['font-space-line-height-s'],
    'font-weight-default': dt.tokens['font-style-weight-400'],
    'font-weight-thick': dt.tokens['font-style-weight-700'],
    fg: dt.tokens['color-body-text'],
    'title-fg': dt.tokens['color-heading-text'],
    'caution-fg': dt.tokens['color-status-alert'],
    'caution-bd': dt.tokens['color-status-alert'],
    'note-bd': dt.tokens['color-container-border-4'],
    'note-fg': dt.tokens['color-label-text'],
    'gen-title-bd': setAlpha(dt.tokens['color-container-background-3'], 0.5),
    'tip-bd': dt.tokens['color-status-info'],
    'tip-title-fg': dt.tokens['color-status-info'],
    'link-fg': dt.tokens['color-action-text-standard-normal']
  }, 'help-popup-content-xis');
  return /*#__PURE__*/_jsx("div", {
    style: tokens,
    className: cn.content,
    onKeyDown: e => markEvent(e),
    onClick: e => e.stopPropagation(),
    children: content
  });
};
const getKey = (docset, topic, anchor) => {
  return `${docset}.${topic}.${anchor}`;
};
const attributesToProps = attributes => {
  const props = {};
  for (let i = 0; i < attributes.length; i++) {
    const attr = attributes.item(i);
    if (!attr) continue;
    if (attr.name === 'class') {
      props.className = attr.value;
    } else {
      props[attr.name] = attr.value;
    }
  }
  return props;
};

// Recursively look through content nodes for help center links
const findHelpCenterLinks = (node, helpCenterLinks) => {
  for (const c of node.childNodes) {
    if (c instanceof Element) {
      const attributes = attributesToProps(c.attributes);
      if (c.tagName === 'A' && attributes['data-help-docset'] && attributes['data-help-topic']) {
        const docsetId = attributes['data-help-docset'];
        const docsetTarget = attributes['data-help-topic'];
        const docsetTargetAnchor = attributes['data-help-anchor'];
        if (docsetTargetAnchor) {
          helpCenterLinks.push({
            docsetId,
            docsetTarget,
            docsetTargetAnchor
          });
        } else {
          helpCenterLinks.push({
            docsetId,
            docsetTarget
          });
        }
      } else {
        findHelpCenterLinks(c, helpCenterLinks);
      }
    }
  }
  return helpCenterLinks;
};
const renderNode = (node, importedLinks, key, resolveSVG, resolveIcon) => {
  const children = [];

  // Loop through the content nodes looking for help center links and icons
  for (const [index, c] of node.childNodes.entries()) {
    if (c instanceof Element) {
      const attributes = attributesToProps(c.attributes);
      if (c.tagName === 'A' && attributes['data-help-docset'] && attributes['data-help-topic']) {
        // We should have imported the necessary information and built the correct url for this help center link
        // Look up that url and create a link pointing to it
        const url = importedLinks[getKey(attributes['data-help-docset'], attributes['data-help-topic'], attributes['data-help-anchor'])];
        if (url) {
          children.push(
          /*#__PURE__*/
          // Links in HelpPopupContent have their own styling, use a normal link here instead of a Nova Link component
          _createElement("a", {
            ...attributes,
            href: url,
            target: "_blank",
            key: index
          }, c.textContent));
        } else {
          children.push(c.textContent);
        }
      } else if (c.tagName === 'BR') {
        children.push( /*#__PURE__*/_jsx("br", {}));
      } else if (c.tagName === 'SPAN' && attributes['data-icon-name']) {
        const createIconName = n => {
          return n.slice(0, 1).toUpperCase() + n.slice(1);
        };
        const name = createIconName(attributes['data-icon-name']);
        const icon = resolveIcon?.(name);
        const svg = resolveSVG?.(name);
        if (icon && icon.name !== 'GenericKnownObject') {
          children.push( /*#__PURE__*/_jsx(Icon, {
            icon: icon,
            ariaLabel: c.ariaLabel ?? undefined,
            tooltip: c.ariaLabel ?? undefined
          }, index));
        } else if (svg && svg.name !== 'GenericKnownObject') {
          children.push( /*#__PURE__*/_jsx(SVG, {
            icon: svg,
            ariaLabel: c.ariaLabel ?? undefined,
            tooltip: c.ariaLabel ?? undefined
            // eslint-disable-next-line @sas/i18n/jsx-to-localize
            ,
            type: name.startsWith('Thumbnail') ? 'thumbnail' : undefined
          }, index));
        } else {
          children.push( /*#__PURE__*/_jsx(Icon, {
            icon: GenericKnownObject
          }, index));
        }
      } else {
        children.push(renderNode(c, importedLinks, index, resolveSVG, resolveIcon));
      }
    } else if (c.textContent) {
      children.push(c.textContent);
    }
  }
  return /*#__PURE__*/createElement(node.tagName.toLowerCase(), {
    key,
    ...attributesToProps(node.attributes)
  }, children);
};

// Parse the incoming html content so we can replace any help center links with nova links
const parseContent = (content, importedLinks, resolveSVG, resolveIcon) => {
  const parser = new DOMParser();
  const parseResults = parser.parseFromString(content, 'text/html');
  return parseResults.body.children.length === 0 ? renderNode(parseResults.body, importedLinks, undefined, resolveSVG, resolveIcon) : /*#__PURE__*/_jsx(Fragment, {
    children: [...parseResults.body.children].map((child, i) => renderNode(child, importedLinks, i, resolveSVG, resolveIcon))
  });
};
//# sourceMappingURL=HelpPopupContent.js.map